import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, signal
} from '@angular/core';
import { of, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { Collection } from 'collection';

export interface IActionModel {
  show?: boolean;
  disabled?: boolean;
  name?: string;
  icon?: string;
  actionClass?: string;
  click?: Function;
}

marker('entry selected');
marker('entries selected');

@Component({
  selector: 'actions-tile',
  templateUrl: './actions-tile.component.html',
  styleUrls: ['./actions-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionsTileComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject();
  @Input() checkboxLabel: string = marker('Select All visible entries');
  @Input() actions: Array<IActionModel>;
  @Input() collection: Collection;
  @Input() selectedSize: number;
  @Input() showAllEntriesCheckbox = false;
  @Output() canceledChanged: EventEmitter<{ isCancel: boolean }> = new EventEmitter();
  @Output() onSelectAll = new EventEmitter();
  @Output() onUnSelectAll = new EventEmitter();
  @Output() allEntriesSelected = new EventEmitter<boolean>();
  strategy$ = of('immediate');
  selectedAllEntries = signal(false);
  allEntriesSelectedInsteadText: string = marker('Select all {{ total }} entries instead');

  get showAllEntries(): boolean {
    return (
      this.showAllEntriesCheckbox &&
      this.collection.totalSelectedItems === this.collection.selectedItems.size &&
      this.collection.meta.pageCount > 1
    );
  }

  constructor(private _cdr: ChangeDetectorRef) {}

  selectAll(e: Event): void | boolean {
    e.preventDefault();
    e.stopPropagation();

    if (this.selectedAllEntries() && this.collection.totalSelectedItems === this.collection.selectedItems.size) {
      return false;
    }

    if (this.collection.totalSelectedItems === this.collection.selectedItems.size) {
      this.collection.unSelectAll();
      this.onUnSelectAll.emit();
    } else {
      this.collection.selectAll();
      this.onSelectAll.emit();
    }
    this.switchAllEntries(false);
  }

  selectAllEntries(e): void {
    e.preventDefault();
    e.stopPropagation();

    this.switchAllEntries(!this.selectedAllEntries());
  }

  switchAllEntries(value: boolean): void {
    this.selectedAllEntries.set(value);
    this.allEntriesSelected.emit(this.selectedAllEntries());
  }

  cancel(e: Event): void {
    e.stopPropagation();
    e.preventDefault();
    this.canceledChanged.next({ isCancel: true });
  }

  ngOnInit(): void {
    this.selectItemSubscription();
    this.collectionSubscription();
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  private selectItemSubscription(): void {
    this.collection.selectedItems$
      .pipe(
        takeUntil(this._destroyed$),
        tap(() => {
          this.switchAllEntries(false);
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private collectionSubscription(): void {
    this.collection.onLoad$
      .pipe(
        takeUntil(this._destroyed$),
        tap(() => {
          if (this.collection.totalSelectedItems !== this.collection.selectedItems.size) {
            this.selectedAllEntries.set(false);
            this._cdr.markForCheck();
          }
        })
      )
      .subscribe();
  }
}
